import { makeAutoObservable, toJS } from 'mobx';
import { FormBuilderStore } from './formBuilderStore';
import { AlgorithmValue, FormBuilderValue, Property } from './models';
import { ModelValidator } from 'framework/mobx-integration';
import { translator } from 'core/localization';
import { apiFetch, ApiResponse } from 'framework/api';
import { TestResultsWindow } from './testResultsWindow';
import React from 'react';
import { ModalPosition, openModal } from 'controls/react/ant/antModal';

const i18n = translator({});

export type AnnotationValidationMethod = 'postValidate' | 'preValidate' | 'test';

export interface TestResult {
	details: {
		attachment: string;
		message: string;
		errorFields: string[];
		success: boolean;
	}[];
	attachment: string;
	id: string;
	errorFields: string[];
	validations: {
		[key: string]: string;
	};
}

export class AnnotationValidator {
	modelValidator: ModelValidator<FormBuilderValue>;
	serverErrors: string[] = [];
	loadErrors = new Map<string, string>();

	constructor(private formBuilderStore: FormBuilderStore) {
		makeAutoObservable(this);
		this.modelValidator = new ModelValidator(formBuilderStore.value);
		this.addValidations();
	}

	addLoadError(property: Property, message: string) {
		this.loadErrors.set(property.id, message);
	}

	clearLoadError(property: Property) {
		this.loadErrors.delete(property.id);
	}

	clearServerError(property: Property) {
		this.serverErrors = this.serverErrors.filter((x) => x != property.id);
	}

	get value() {
		return this.formBuilderStore.value;
	}

	addValidations() {
		Object.keys(this.value).forEach((key) => {
			const property = this.formBuilderStore.propertyById(key);
			if (!property) {
				return;
			}
			if (property.required) {
				if (property.type === 'CECollection') {
					this.modelValidator.add(key, {
						callback: () => (this.value[key] as FormBuilderValue[]).length > 0,
						reactionExpression: () => (this.value[key] as FormBuilderValue[]).length,
						message: i18n('Required field'),
					});
				} else {
					this.modelValidator.required(key, null, null, false);
				}
			}
			if (property.type === 'CECollection') {
				this.modelValidator.add(key, {
					callback: () => {
						const stores = this.formBuilderStore.subStores.get(key);
						return stores.every((x) => x.valid);
					},
					reactionExpression: () => {
						const stores = this.formBuilderStore.subStores.get(key);
						return stores.every((x) => x.valid);
					},
					message: i18n('Invalid child'),
				});
			}
			if (property.type === 'CEAlgorithm') {
				// ported from the old formBuilder's ceAlgorithm.js validatePercentile/validateDays; only applies
				// when PercentileAlgorithm is the selected type, same as the old field-level `validate` props.
				this.modelValidator.addGeneric(`${key}.percentile`, {
					reactionExpression: () => (this.value[key] as AlgorithmValue)?.percentile,
					callback: () => {
						const algorithm = this.value[key] as AlgorithmValue;
						if (algorithm?.type !== 'PercentileAlgorithm') return true;
						return algorithm.percentile != null && algorithm.percentile >= 0 && algorithm.percentile <= 100;
					},
					message: i18n('Value is out of 0-100'),
				});
				this.modelValidator.addGeneric(`${key}.days`, {
					reactionExpression: () => (this.value[key] as AlgorithmValue)?.days,
					callback: () => {
						const algorithm = this.value[key] as AlgorithmValue;
						if (algorithm?.type !== 'PercentileAlgorithm') return true;
						return (algorithm.days ?? 0) >= 1;
					},
					message: i18n('Value is out of >= 1'),
				});
			}
			this.modelValidator.add(key, {
				callback: () => !this.serverErrors.includes(key),
				reactionExpression: () => toJS(this.serverErrors),
			});
		});
	}

	get configuration() {
		return this.formBuilderStore.configuration;
	}
	get hasTest() {
		return this.configuration.hasTest;
	}

	get hasPreValidate() {
		return this.configuration.hasPreValidate;
	}

	get hasPostValidate() {
		return this.configuration.hasPostValidate;
	}

	innerTest = async (method: AnnotationValidationMethod, showOkResult: boolean = false) => {
		const request = this.formBuilderStore.options.validateMethods[method](this.value);
		if (method == 'test') {
			request.suppressErrorMessage = true;
		}
		const result = await apiFetch(request);

		if (result.data) {
			const errorFields = result.data.errorFields;
			this.serverErrors = errorFields;
			this.modelValidator.validate();
		}

		if (showOkResult || !result.success) {
			this.showTestResults(result);
		}

		return result.success;
	};

	preValidate = async () => {
		if (!this.hasPreValidate) {
			return true;
		}
		return this.innerTest('preValidate');
	};

	postValidate = async () => {
		if (!this.hasPostValidate) {
			return true;
		}
		return this.innerTest('postValidate');
	};

	test = async () => {
		if (!this.hasTest) {
			return true;
		}
		return this.innerTest('test', true);
	};

	showTestResults(result: ApiResponse<TestResult>) {
		openModal(
			{
				closable: true,
				title: 'Test results',
				positionType: ModalPosition.Centered,
				defaultFooterButtons: ['ok'],
				okText: 'Ok',
				width: 520,
			},
			<TestResultsWindow result={result} />,
		);
	}

	get valid() {
		return this.modelValidator.valid;
	}

	get errors() {
		return [...this.loadErrors.entries()].map(([key, value]) => ({ property: key, error: value }));
	}
}
